import { Icon } from '@capsico/ui';
import { Button } from './ui';

/**
 * Floating cart bar (pattern C over the menu / home): "2 items · ₹420" on the left, "View cart"
 * on the right, yellow with ink text. Sits above the bottom nav when the nav is showing.
 * Presentation only.
 */
export function CartBar({
  countText,
  amountText,
  outletName,
  actionLabel,
  aboveNav,
  onOpen,
}: {
  countText: string;
  amountText: string;
  outletName: string | null;
  actionLabel: string;
  aboveNav: boolean;
  onOpen: () => void;
}) {
  return (
    <div
      className={`fixed inset-x-0 z-40 mx-auto w-full max-w-120 px-3 ${
        aboveNav
          ? 'bottom-20 mb-[env(safe-area-inset-bottom)]'
          : 'bottom-3 mb-[env(safe-area-inset-bottom)]'
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex h-14 w-full items-center gap-3 rounded-xl bg-brand px-4 text-left text-on-brand shadow-[0_6px_18px_rgba(15,23,42,0.18)] active:scale-[0.99]"
      >
        <span className="inline-flex size-9 items-center justify-center rounded-lg bg-on-brand/10">
          <Icon name="bag" size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold tabular">
            {countText} · {amountText}
          </span>
          {outletName && (
            <span className="block truncate text-caption opacity-80">{outletName}</span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-sm font-bold">
          {actionLabel}
          <Icon name="chevron-right" size={16} />
        </span>
      </button>
    </div>
  );
}

/** Centred confirm dialog (pattern U): title, body, secondary + primary buttons. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
      role="alertdialog"
      aria-label={title}
    >
      <button
        type="button"
        aria-label={cancelLabel}
        onClick={onCancel}
        className="absolute inset-0 bg-scrim animate-[fade_160ms_ease-out]"
      />
      <div className="relative w-full max-w-sm rounded-sheet bg-surface p-5 animate-[rise_180ms_ease-out]">
        <h2 className="text-lg font-bold text-ink">{title}</h2>
        <p className="mt-2 text-sm leading-5 text-ink-2">{body}</p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button tone="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}
